<script lang="ts">
  import { onDestroy } from 'svelte';
  import { cn } from '@/lib/utils';
  import { Button } from '@/lib/components/ui/button';
  import { Card } from '@/lib/components/ui/card';
  import { Skeleton } from '@/lib/components/ui/skeleton';
  import * as Dialog from '$lib/components/ui/dialog';
  import type { Exercise } from '@/Common/ActivityFunctions';
  import ActivityCardActions from './ActivityCardActions.svelte';
  import ActivityCardLong from './ActivityCardLong.svelte';
  import ActivityCardSquare from './ActivityCardSquare.svelte';
  import ActivitySearchFilter from './ActivitySearchFilter.svelte';
  import type { ActivityFilterFields, ActivityOwnerOption } from './ActivityFilterFields';

  export let fields: ActivityFilterFields;
  /** The coach/squad roster the member filter offers; empty on the personal dashboard. */
  export let owners: ActivityOwnerOption[] = [];
  export let ownersTitle: string = 'Athletes';
  export let activities: Exercise[] = [];
  /** True only for the first page of a new filter, when there is nothing on screen yet. */
  export let isLoading: boolean = false;
  /** True while another page is being appended below what's already shown. */
  export let isLoadingMore: boolean = false;
  /**
   * A pushed change (upload, edit, delete elsewhere) is rebuilding the list underneath the athlete.
   * Deliberately not `isLoading`: nothing is torn down, so it shows as a spinner beside the count
   * rather than skeletons, and the list neither empties nor scrolls back to the top.
   */
  export let isResyncing: boolean = false;
  export let loadFailed: boolean = false;

  export let totalResults: number = 0;
  export let hasMore: boolean = false;
  export let onLoadMore: () => void = () => {};

  export let onViewDetails: ((exercise: Exercise) => void) | null = null;
  export let onDelete: (exercise: Exercise) => void = () => {};
  export let onChange: (exercise: Exercise, changes: Partial<Exercise>) => void = () => {};
  export let onRefresh: () => void = () => {};
  /** Set on the my-fitness tab, where scheduling means switching tabs rather than navigating. */
  export let onScheduleActivity: (() => void) | null = null;
  /** Coach/squad dashboards list several athletes, so each card names their owner. */
  export let showOwner: boolean = false;

  /**
   * How far down the sticky filter sidebar has to start, i.e. the bottom edge of whatever else is
   * already stuck to the top of the page. That's just the site header on /activities, but the
   * my-fitness tab bar is sticky below it too, and the sidebar would otherwise slide underneath.
   */
  export let stickyTop: string = '46px';

  let viewType: 'grid' | 'list' = 'list';
  let filterDialogOpen = false; // mobile only
  let contentElement: HTMLElement | null = null;

  // The desktop sidebar collapses to a rail, so a narrow window (or the my-fitness tab, which is
  // already sharing its width) can give the cards the whole column. Remembered across visits, since
  // whichever way an athlete works is the way they keep working.
  const COLLAPSE_KEY = 'activitiesDashFiltersCollapsed';
  let filtersCollapsed = readCollapsed();
  function readCollapsed(): boolean {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }
  function toggleFilters() {
    filtersCollapsed = !filtersCollapsed;
    try {
      window.localStorage.setItem(COLLAPSE_KEY, filtersCollapsed ? '1' : '0');
    } catch (error) {
      // Private browsing; the preference just doesn't outlive the page.
    }
  }

  // Infinite scroll. The sentinel sits below the last card; whenever it comes into view -- with a
  // screen's worth of margin so the next page is already arriving by the time the athlete gets
  // there -- ask for more. The "Load more" button below it stays as the keyboard/no-observer path.
  let sentinel: HTMLElement | null = null;
  let observer: IntersectionObserver | null = null;
  // Re-attached after every append: an observer only reports *changes* in intersection, so a page
  // of results too short to push the sentinel off screen would otherwise stall the scroll. Starting
  // a fresh observation re-reports the current state and keeps filling until the viewport is full.
  $: watchSentinel(sentinel, activities.length, hasMore, isLoadingMore, isResyncing, loadFailed);
  function watchSentinel(element: HTMLElement | null, ..._retriggers: unknown[]) {
    observer?.disconnect();
    observer = null;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) requestMore();
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(element);
  }
  onDestroy(() => observer?.disconnect());

  function requestMore() {
    // loadFailed stops the automatic retry loop; the "Try again" button calls onLoadMore directly.
    if (!hasMore || isLoading || isLoadingMore || isResyncing || loadFailed) return;
    onLoadMore();
  }

  // A fresh *first* page should start at the top of the list. Keyed off the end of a fetch rather
  // than off `activities`, which also changes when a card mutates itself (favouriting one
  // shouldn't yank the list back to the top) and when a later page is appended.
  let wasLoading = false;
  $: scrollToTopAfterLoad(isLoading);
  function scrollToTopAfterLoad(loading: boolean) {
    const finishedLoading = wasLoading && !loading;
    wasLoading = loading;
    if (!finishedLoading || !contentElement) return;
    // Depending on the host page this column is either its own scroll container or just part of the
    // document flow, so handle both -- and in the page-scroll case stop short of the sticky header.
    if (contentElement.scrollTop > 0) {
      contentElement.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const target = contentElement.getBoundingClientRect().top + window.scrollY - (parseInt(stickyTop, 10) || 0);
    if (window.scrollY > target) window.scrollTo({ top: target, behavior: 'smooth' });
  }
</script>

<div
  class={cn(
    'hidden lg:flex flex-col sticky h-full shrink-0 border-solid border-0 border-r border-border bg-muted/30 overflow-x-hidden',
    filtersCollapsed ? 'w-[42px] px-1 py-2' : 'w-[320px] py-4'
  )}
  style="top: {stickyTop}; max-height: calc(100dvh - {stickyTop} - 1rem); {filtersCollapsed
    ? `min-height: calc(100dvh - ${stickyTop} - 1rem);`
    : ''}"
>
  <div class={cn('flex flex-row items-center gap-2 px-4', filtersCollapsed ? 'justify-center' : 'justify-between mb-2')}>
    {#if !filtersCollapsed}
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Filters</h3>
    {/if}
    <Button
      variant="ghost"
      size="icon"
      class="text-muted-foreground"
      title={filtersCollapsed ? 'Show filters' : 'Hide filters'}
      on:click={toggleFilters}
    >
      <i class={cn('fa-solid relative', filtersCollapsed ? 'fa-sliders' : 'fa-chevron-left')} aria-hidden="true">
        <!-- Collapsed, the rail is the only sign that a filter is still narrowing the list. -->
        {#if filtersCollapsed && fields?.isFilterApplied()}
          <div class="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
        {/if}
      </i>
    </Button>
  </div>
  {#if !filtersCollapsed}
    <!-- Only the fields scroll: Reset stays pinned to the bottom of the sidebar, so it's reachable
         without scrolling past every filter first. Outside the scroll area, so it needs no
         background of its own to mask what passes under it. -->
    <div class="flex-1 min-h-0 px-4 overflow-y-auto overflow-x-hidden">
      <ActivitySearchFilter bind:fields {owners} {ownersTitle} />
    </div>
    <div class="shrink-0 pt-3 mt-1 px-4 border-solid border-0 border-t border-border">
      <Button variant="outline" class="w-full" on:click={() => { fields.resetFilters(); fields = fields; }}>
        Reset
      </Button>
    </div>
  {/if}
</div>

<div bind:this={contentElement} class="h-full flex-1 min-w-0 flex flex-col gap-2 overflow-y-auto overflow-x-hidden py-4 px-2 md:px-4 lg:px-0">
  {#if !showOwner}
    <div class="flex flex-row gap-1 items-center justify-end w-full">
      {#if onScheduleActivity}
        <Button size="sm" variant="outline" on:click={onScheduleActivity}>
          <i class="fa-regular fa-calendar-plus text-xs mr-1" aria-hidden="true"></i>Schedule an Activity
        </Button>
      {:else}
        <Button size="sm" variant="outline" class="text-foreground no-underline" href="/my-fitness">
          <i class="fa-regular fa-calendar-plus text-xs mr-1" aria-hidden="true"></i>Schedule an Activity
        </Button>
      {/if}
      <Button size="sm" variant="outline" class="text-foreground no-underline" href="/activities/upload" target="activity">
        <i class="fa-regular fa-arrow-up-from-bracket text-xs mr-1" aria-hidden="true"></i>Upload Activity
      </Button>
    </div>
  {/if}

  <div class="lg:hidden flex flex-row gap-2 items-center">
    <Button variant="outline" class="space-x-1 flex-1" on:click={() => (filterDialogOpen = true)}>
      <span>Filters</span>
      <i class="fa-light fa-sliders-up relative">
        {#if fields?.isFilterApplied()}
          <div class="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
        {/if}
      </i>
    </Button>
    <Button variant={viewType === 'list' ? 'outline' : 'ghost'} class={cn(viewType === 'list' ? 'text-black' : 'text-muted-foreground')} size="icon" on:click={() => (viewType = 'list')}>
      <i class="fa-light fa-list-ul" aria-hidden="true" />
    </Button>
    <Button variant={viewType === 'grid' ? 'outline' : 'ghost'} class={cn(viewType === 'grid' ? 'text-black' : 'text-muted-foreground')} size="icon" on:click={() => (viewType = 'grid')}>
      <i class="fa-light fa-grid-2" aria-hidden="true" />
    </Button>
  </div>

  <div class="flex flex-row items-center justify-between gap-2 min-h-[36px] px-2">
    <div class="text-sm text-muted-foreground">
      <!-- How many the filter matched. How many of those have been scrolled into view isn't
           something the athlete has to keep track of -- the list just keeps going. -->
      {#if !isLoading}
        {totalResults} {totalResults === 1 ? 'activity' : 'activities'}
        {#if isResyncing}
          <i class="fa-solid fa-spinner fa-spin ml-1 text-xs" aria-hidden="true"></i>
          <span class="sr-only">Updating</span>
        {/if}
      {/if}
    </div>
    <div class="hidden lg:flex flex-row gap-1">
      <Button variant={viewType === 'list' ? 'outline' : 'ghost'} class={cn(viewType === 'list' ? 'text-black' : 'text-muted-foreground')} size="icon" on:click={() => (viewType = 'list')}>
        <i class="fa-light fa-list-ul" aria-hidden="true" />
      </Button>
      <Button variant={viewType === 'grid' ? 'outline' : 'ghost'} class={cn(viewType === 'grid' ? 'text-black' : 'text-muted-foreground')} size="icon" on:click={() => (viewType = 'grid')}>
        <i class="fa-light fa-grid-2" aria-hidden="true" />
      </Button>
    </div>
  </div>

  {#if isLoading}
    <div class={cn(viewType === 'grid' ? 'flex flex-wrap gap-4 justify-center items-start' : 'flex flex-col gap-4 px-1')}>
      {#each Array(viewType === 'grid' ? 6 : 4) as _}
        {#if viewType === 'grid'}
          <div class="w-72 h-72 flex flex-col gap-2">
            <Skeleton class="w-full flex-1" />
            <Skeleton class="w-[60%] h-5" />
            <div class="w-full flex flex-row justify-between">
              <Skeleton class="w-[30%] h-5" />
              <Skeleton class="w-[30%] h-5" />
            </div>
          </div>
        {:else}
          <div class="w-full h-40 flex flex-row gap-4">
            <Skeleton class="w-[320px] h-full shrink-0" />
            <div class="flex-1 flex flex-col gap-2">
              <Skeleton class="w-[40%] h-5" />
              <Skeleton class="w-[25%] h-4" />
              <Skeleton class="w-full flex-1" />
            </div>
          </div>
        {/if}
      {/each}
    </div>
  {:else if loadFailed && activities.length === 0}
    <div class="flex flex-col gap-1 items-center my-8 text-center">
      <div class="text-gray-600">We couldn't load your activities. Please try again.</div>
    </div>
  {:else if activities.length === 0}
    <div class="flex flex-col gap-1 items-center my-8 text-center">
      <div class="text-gray-600">No activities found.</div>
      {#if fields?.isFilterApplied()}
        <div class="text-gray-500">Check any filters you might have applied.</div>
        <Button variant="outline" size="sm" class="mt-2" on:click={() => { fields.resetFilters(); fields = fields; }}>
          Reset filters
        </Button>
      {/if}
    </div>
  {:else if viewType === 'grid'}
    <div class="flex flex-wrap gap-4 justify-center items-start">
      {#each activities as activity (activity.path)}
        <Card
          class={cn(
            'border-border p-[8px] pt-[12px]',
            onViewDetails
              ? 'cursor-pointer transition-all duration-150 ease-out transform-gpu will-change-transform hover:shadow-lg hover:-translate-y-0.5 hover:bg-muted'
              : 'shadow-sm hover:shadow'
          )}
          on:click={() => onViewDetails?.(activity)}
        >
          <div class="w-full space-y-1">
            <ActivityCardSquare size="lg" exercise={activity} {showOwner} />
            <div class="flex flex-row justify-center border-solid border-0 border-t border-border pt-1">
              <ActivityCardActions exercise={activity} {onDelete} {onChange} {onRefresh} showSendButtons={false} />
            </div>
          </div>
        </Card>
      {/each}
    </div>
  {:else}
    <div class="flex flex-col gap-4 px-1">
      {#each activities as activity (activity.path)}
        <Card
          class="border-border p-[12px] shadow-sm hover:shadow"
        >
          <ActivityCardLong exercise={activity} {onViewDetails} {onDelete} {onChange} {onRefresh} {showOwner} />
        </Card>
      {/each}
    </div>
  {/if}

  {#if !isLoading && activities.length > 0}
    <div bind:this={sentinel} class="flex flex-col items-center gap-2 py-4">
      {#if isLoadingMore}
        <i class="fa-solid fa-spinner fa-spin text-gray-400" aria-hidden="true"></i>
      {:else if loadFailed}
        <div class="text-gray-600 text-sm">We couldn't load any more activities.</div>
        <Button variant="outline" size="sm" on:click={onLoadMore}>Try again</Button>
      {:else if hasMore}
        <Button variant="outline" size="sm" on:click={onLoadMore}>Load more</Button>
      {:else}
        <div class="text-gray-500 text-sm">That's all {totalResults} {totalResults === 1 ? 'activity' : 'activities'}.</div>
      {/if}
    </div>
  {/if}
</div>

<!-- Filters dialog for mobile view -->
<Dialog.Root bind:open={filterDialogOpen} portal="#shadcn-ui-dialog-target">
  <Dialog.Content class="flex flex-col max-h-[95dvh] md:h-[600px] max-w-[420px] w-[95vw] overflow-hidden rounded-xl px-3">
    <Dialog.Header class="px-3">
      <Dialog.Title>Filters</Dialog.Title>
    </Dialog.Header>
    <div class="overflow-y-auto px-3">
      <ActivitySearchFilter bind:fields {owners} {ownersTitle} />
    </div>
    <Dialog.Footer class="gap-2 flex-row justify-end">
      <Button
        variant="outline"
        on:click={() => {
          if (!fields) return;
          fields.resetFilters();
          fields = fields;
        }}
      >
        Reset
      </Button>
      <Button variant="outline" on:click={() => (filterDialogOpen = false)}>Apply</Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
